import React, { useEffect, useState, ChangeEvent, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ScorecardDetailsViewModel from './Viewmodels/ScorecardDetailsViewModel';
import { ValidatorForm, SelectValidator, TextValidator } from 'react-material-ui-form-validator';
import { useSnackbar } from 'notistack';
import Grid from '@mui/material/Grid';
import { MsalContext } from '../Contexts/MsalContext';
import MenuItem from '@mui/material/MenuItem';
import { APIContext } from '../Contexts/APIContext';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';

type Props = {
    open: boolean;
    onClose: (refresh: boolean, templateId?: number) => void;
    editId: number;
}

export default function AddEditScorecardDetailsModal({ open, onClose, editId }: Props) {
    const { get, post } = useContext(APIContext);
    const { enqueueSnackbar } = useSnackbar();
    const { msalFetch } = useContext(MsalContext);
    const [details, setDetails] = useState(new ScorecardDetailsViewModel());
    const [clientList, setClientList] = useState<string[]>([]);
    const [channelList, setChannelList] = useState<string[]>([]);
    const [enablePassThreshold, setEnablePassThreshold] = useState(false);
    const [enableResQPassThreshold, setEnableResQPassThreshold] = useState(false);

    useEffect(() => {
        getClients();
    }, []);

    useEffect(() => {
        if (!open) setDetails(new ScorecardDetailsViewModel());
    }, [open]);

    useEffect(() => {
        if (details.client !== '') getChannels();
    }, [details.client]);

    useEffect(() => {
        if (editId !== 0 && open) getData();
    }, [editId, open]);

    const getClients = () => {
        msalFetch('api/User/GetClientList')
            .then(response => response.json())
            .then(data => {
                setClientList(data);
                if (data.length < 1) {
                    msalFetch('api/User/GetClientsClient')
                        .then(res => res.json())
                        .then(resp => {
                            setClientList(resp);
                        })
                }
            });
    }

    const getChannels = () => {
        msalFetch(`api/User/GetChannelList?client=${details.client}`)
            .then(res => res.json())
            .then(resp => {
                setChannelList(resp);
            })
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setDetails({
            ...details,
            [e.target.name]: e.target.value
        });
    }

    const handleCheckedChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.name === 'enablePassThreshold') {
            setEnablePassThreshold(e.target.checked);
            if (!e.target.checked) setDetails({
                ...details,
                passThreshold: null
            });
        }
        else if (e.target.name === 'enableResQPassThreshold') {
            setEnableResQPassThreshold(e.target.checked);
            if (!e.target.checked) setDetails({
                ...details,
                resQPassThreshold: null
            });
        }
        else {
            setDetails({
                ...details,
                [e.target.name]: e.target.checked
            });
        }
    }

    const clearDetail = (detail: keyof ScorecardDetailsViewModel) => {
        setDetails(prev => ({
            ...prev,
            [detail]: ''
        }))
    }

    const saveScorecard = () => {
        enqueueSnackbar(editId !== 0 ? "Editing scorecard details, please wait..." : "Creating new scorecard, please wait...", { variant: 'info' });
        post('ScorecardManagement', `AddEditScorecardTemplate` + (editId > 0 ? `?oldTemplateId=${editId}` : ""), details, (data) => {
            onClose(true, data);
        });
    }

    const getData = () => {
        get('ScorecardManagement', `GetScorecardTemplateDetails?templateId=${editId}`, (data) => {
            setEnablePassThreshold(data.passThreshold !== null ? true : false);
            setEnableResQPassThreshold(data.resQPassThreshold !== null ? true : false);
            setDetails(data);
        });
    }

    return (
        <Dialog
            open={open}
            onClose={() => onClose(false)}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>{editId > 0 ? "Edit Template" : "Add New Template"}</DialogTitle>
            <ValidatorForm onSubmit={saveScorecard}>
                <DialogContent>
                    <Grid container spacing={1}>
                        <Alert severity='warning'>{
                            details.hasBeenActive ?
                                'This template has been made active at least once, editing this template will create a new one with values copied from the original'
                                : 'This template had never been active, editing this will edit the original template'
                        }</Alert>
                        <Grid size={12}>
                            <InputLabel shrink>Name</InputLabel>
                            <TextValidator
                                name='name'
                                fullWidth
                                value={details.name}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                margin="none"
                                validators={['required']}
                                errorMessages={['This field is required']}
                            />
                        </Grid>
                        <Grid size={4}>
                            <InputLabel shrink>Client</InputLabel>
                            <SelectValidator
                                name='client'
                                fullWidth
                                value={details.client}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                margin="none"
                                validators={['required']}
                                errorMessages={['This field is required']}
                            >
                                {clientList.map((client: string, index: number) => {
                                    return (<MenuItem key={index} value={client}>{client}</MenuItem>);
                                })}
                            </SelectValidator>
                        </Grid>
                        <Grid size={4}>
                            <InputLabel shrink>Channel</InputLabel>
                            <SelectValidator
                                name='channel'
                                fullWidth
                                value={details.channel}
                                onChange={handleChange}
                                disabled={details.client === ''}
                                variant="outlined"
                                size="small"
                                margin="none"
                                InputProps={{
                                    endAdornment: (
                                        details.channel &&
                                        <IconButton
                                            onClick={() => clearDetail('channel')}
                                            sx={{ marginRight: 2 }}>
                                            <ClearIcon />
                                        </IconButton>
                                    )
                                }}
                            >
                                {channelList.map((channel: string, index: number) => {
                                    return (<MenuItem key={index} value={channel}>{channel}</MenuItem>);
                                })}
                            </SelectValidator>
                        </Grid>
                        <Grid size={4}>
                            <InputLabel shrink>Scorecard Type</InputLabel>
                            <SelectValidator
                                name='scorecardType'
                                fullWidth
                                value={details.scorecardType}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                margin="none"
                                validators={['required']}
                                errorMessages={['This field is required']}
                            >
                                <MenuItem key={1} value='Monitoring'>Monitoring</MenuItem>
                                <MenuItem key={2} value='Alert'>Alert</MenuItem>
                            </SelectValidator>
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel control={<Switch name='enablePassThreshold' checked={enablePassThreshold} onChange={handleCheckedChange} />} label="Enable Pass Threshold" labelPlacement="end" />
                        </Grid>
                        {enablePassThreshold &&
                            <Grid size={12}>
                                <InputLabel shrink>Pass Threshold</InputLabel>
                                <TextValidator
                                    name='passThreshold'
                                    fullWidth
                                    value={details.passThreshold}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                    margin="none"
                                    validators={['required']}
                                    errorMessages={['This field is required']}
                                />
                            </Grid>
                        }
                        <Grid size={12}>
                            <FormControlLabel control={<Switch name='enableResQPassThreshold' checked={enableResQPassThreshold} onChange={handleCheckedChange} />} label="Enable ResQ Pass Threshold" labelPlacement="end" />
                        </Grid>
                        {enableResQPassThreshold &&
                            <Grid size={12}>
                                <InputLabel shrink>ResQ Pass Threshold</InputLabel>
                                <TextValidator
                                    name='resQPassThreshold'
                                    fullWidth
                                    value={details.resQPassThreshold}
                                    onChange={handleChange}
                                    variant="outlined"
                                    size="small"
                                    margin="none"
                                    validators={['required']}
                                    errorMessages={['This field is required']}
                                />
                            </Grid>
                        }
                        <Grid size={6}>
                            <InputLabel shrink>Customer Level</InputLabel>
                            <Switch
                                name='customerLevel'
                                checked={details.customerLevel}
                                onChange={handleCheckedChange}
                            />
                        </Grid>
                        <Grid size={6}>
                            <InputLabel shrink>Delete On Challenge Upheld</InputLabel>
                            <Switch
                                name='deleteOnChallengeUpheld'
                                checked={details.deleteOnChallengeUpheld}
                                onChange={handleCheckedChange}
                            />
                        </Grid>
                        <Grid size={6}>
                            <InputLabel shrink>Enable Levelling</InputLabel>
                            <Switch
                                name='enableLevelling'
                                checked={details.enableLevelling}
                                onChange={handleCheckedChange}
                            />
                        </Grid><Grid size={6}>
                            <InputLabel shrink>Enable Calibration</InputLabel>
                            <Switch
                                name='enableCalibration'
                                checked={details.enableCalibration}
                                onChange={handleCheckedChange}
                            />
                        </Grid>
                    </Grid><Grid size={6}>
                        <InputLabel shrink>Enable QA Copy</InputLabel>
                        <Switch
                            name='enableQACopy'
                            checked={details.enableQACopy}
                            onChange={handleCheckedChange}
                        />
                    </Grid>
                    <DialogActions>
                        <Button onClick={() => onClose(false)}>Cancel</Button>
                        <Button type="submit">Save</Button>
                    </DialogActions>
                </DialogContent>
            </ValidatorForm>
        </Dialog>
    )
}